import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import AdminLayout from '../../components/admin/AdminLayout';
import StatusBadge from '../../components/shared/StatusBadge';
import {
  MonitoringOverview,
  MonitoringAlerts,
  MonitoringAnomalies,
  MonitoringPredictions,
  MonitoringThresholds,
  MonitoringWebhooks,
  MonitoringReports
} from './monitoring';
import {
  Activity,
  RefreshCw,
  Clock,
  AlertTriangle,
  XCircle,
  Wifi,
  WifiOff,
  Bell,
  BellRing,
  Settings,
  Link2,
  FileText,
  Search,
  TrendingUp
} from 'lucide-react';

const AdminMonitoring = () => {
  // State Management
  const [metrics, setMetrics] = useState(null);
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [healthHistory, setHealthHistory] = useState(null);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [thresholds, setThresholds] = useState(null);
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Saving states
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [savingWebhooks, setSavingWebhooks] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(null);
  const [checkingAlerts, setCheckingAlerts] = useState(false);
  
  // Reports state
  const [reportConfig, setReportConfig] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [savingReportConfig, setSavingReportConfig] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(null);
  const [previewReport, setPreviewReport] = useState(null);
  
  // Anomaly state
  const [anomalyConfig, setAnomalyConfig] = useState(null);
  const [anomalyHistory, setAnomalyHistory] = useState([]);
  const [anomalyBaselines, setAnomalyBaselines] = useState(null);
  const [savingAnomalyConfig, setSavingAnomalyConfig] = useState(false);
  const [checkingAnomalies, setCheckingAnomalies] = useState(false);
  const [retrainingBaselines, setRetrainingBaselines] = useState(false);
  
  // Prediction state
  const [predictionConfig, setPredictionConfig] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [predictionHistory, setPredictionHistory] = useState([]);
  const [savingPredictionConfig, setSavingPredictionConfig] = useState(false);
  const [generatingPredictions, setGeneratingPredictions] = useState(false);

  // Fetch all metrics
  const fetchMetrics = useCallback(async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const [metricsRes, realtimeRes, historyRes, alertsRes, alertHistoryRes, thresholdsRes, webhooksRes, reportConfigRes, reportHistoryRes, anomalyConfigRes, anomalyHistoryRes, anomalyBaselinesRes, predictionConfigRes, predictionsRes, predictionHistoryRes] = await Promise.all([
        api.get('/admin/system/metrics'),
        api.get('/admin/system/realtime-stats'),
        api.get('/admin/system/health-history'),
        api.get('/admin/alerts/active'),
        api.get('/admin/alerts/history?limit=20'),
        api.get('/admin/alerts/thresholds'),
        api.get('/admin/alerts/webhooks'),
        api.get('/admin/reports/config'),
        api.get('/admin/reports/history?limit=5'),
        api.get('/admin/anomalies/config'),
        api.get('/admin/anomalies/history?limit=30'),
        api.get('/admin/anomalies/baselines'),
        api.get('/admin/predictions/config'),
        api.get('/admin/predictions'),
        api.get('/admin/predictions/history?limit=10')
      ]);
      
      setMetrics(metricsRes.data);
      setRealtimeStats(realtimeRes.data);
      setHealthHistory(historyRes.data);
      setActiveAlerts(alertsRes.data?.alerts || []);
      setAlertHistory(alertHistoryRes.data?.alerts || []);
      setThresholds(thresholdsRes.data?.thresholds || {});
      setWebhookConfig(webhooksRes.data?.webhooks || {});
      setReportConfig(reportConfigRes.data?.config || {});
      setReportHistory(reportHistoryRes.data?.reports || []);
      setAnomalyConfig(anomalyConfigRes.data?.config || {});
      setAnomalyHistory(anomalyHistoryRes.data?.anomalies || []);
      setAnomalyBaselines(anomalyBaselinesRes.data || {});
      setPredictionConfig(predictionConfigRes.data?.config || {});
      setPredictions(predictionsRes.data || {});
      setPredictionHistory(predictionHistoryRes.data?.predictions || []);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError('Failed to fetch system metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchMetrics, 10000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchMetrics]);

  // Alert handlers
  const handleManualAlertCheck = async () => {
    setCheckingAlerts(true);
    try {
      const api = (await import('../../utils/api')).default;
      const res = await api.post('/admin/alerts/check');
      if (res.data?.newAlerts?.length > 0) {
        setActiveAlerts(prev => [...res.data.newAlerts, ...prev]);
      }
      await fetchMetrics();
    } catch (err) {
      console.error('Error checking alerts:', err);
    } finally {
      setCheckingAlerts(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.post(`/admin/alerts/${alertId}/acknowledge`);
      setActiveAlerts(prev => prev.filter(a => a.id !== alertId));
      await fetchMetrics();
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  // Threshold handlers
  const handleSaveThresholds = async () => {
    setSavingThresholds(true);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/admin/alerts/thresholds', thresholds);
      await fetchMetrics();
    } catch (err) {
      console.error('Error saving thresholds:', err);
    } finally {
      setSavingThresholds(false);
    }
  };

  const updateThreshold = (key, value) => {
    setThresholds(prev => ({
      ...prev,
      [key]: parseInt(value) || 0
    }));
  };

  // Webhook handlers
  const handleSaveWebhooks = async () => {
    setSavingWebhooks(true);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/admin/alerts/webhooks', webhookConfig);
      await fetchMetrics();
    } catch (err) {
      console.error('Error saving webhooks:', err);
    } finally {
      setSavingWebhooks(false);
    }
  };

  const handleTestWebhook = async (type) => {
    setTestingWebhook(type);
    try {
      const api = (await import('../../utils/api')).default;
      await api.post(`/admin/alerts/webhooks/test/${type}`);
      alert(`Test message sent to ${type.charAt(0).toUpperCase() + type.slice(1)}!`);
    } catch (err) {
      console.error('Error testing webhook:', err);
      alert(`Failed to send test message: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTestingWebhook(null);
    }
  };

  const updateWebhookConfig = (key, value) => {
    setWebhookConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Report handlers
  const updateReportConfig = (key, value) => {
    setReportConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveReportConfig = async () => {
    setSavingReportConfig(true);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/admin/reports/config', reportConfig);
      await fetchMetrics();
    } catch (err) {
      console.error('Error saving report config:', err);
    } finally {
      setSavingReportConfig(false);
    }
  };

  const handleGenerateReport = async (type) => {
    setGeneratingReport(type);
    try {
      const api = (await import('../../utils/api')).default;
      await api.post(`/admin/reports/generate/${type}`);
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} report generated and sent!`);
      await fetchMetrics();
    } catch (err) {
      console.error('Error generating report:', err);
      alert(`Failed to generate report: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGeneratingReport(null);
    }
  };

  const handlePreviewReport = async (type) => {
    try {
      const api = (await import('../../utils/api')).default;
      const res = await api.get(`/admin/reports/preview/${type}`);
      setPreviewReport(res.data?.report || null);
    } catch (err) {
      console.error('Error previewing report:', err);
    }
  };

  // Anomaly handlers
  const updateAnomalyConfig = (key, value) => {
    setAnomalyConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const updateAnomalyMetric = (metric, enabled) => {
    setAnomalyConfig(prev => ({
      ...prev,
      metrics_to_monitor: {
        ...prev.metrics_to_monitor,
        [metric]: enabled
      }
    }));
  };

  const handleSaveAnomalyConfig = async () => {
    setSavingAnomalyConfig(true);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/admin/anomalies/config', anomalyConfig);
      await fetchMetrics();
    } catch (err) {
      console.error('Error saving anomaly config:', err);
    } finally {
      setSavingAnomalyConfig(false);
    }
  };

  const handleCheckAnomalies = async () => {
    setCheckingAnomalies(true);
    try {
      const api = (await import('../../utils/api')).default;
      const res = await api.post('/admin/anomalies/check');
      if (res.data?.anomalies?.length > 0) {
        alert(`${res.data.anomalies.length} anomaly(ies) detected!`);
      } else {
        alert('No anomalies detected.');
      }
      await fetchMetrics();
    } catch (err) {
      console.error('Error checking anomalies:', err);
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setCheckingAnomalies(false);
    }
  };

  const handleRetrainBaselines = async () => {
    setRetrainingBaselines(true);
    try {
      const api = (await import('../../utils/api')).default;
      const res = await api.post('/admin/anomalies/retrain');
      alert(`Baselines recalculated for ${Object.keys(res.data?.baselines || {}).length} metrics`);
      await fetchMetrics();
    } catch (err) {
      console.error('Error retraining baselines:', err);
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRetrainingBaselines(false);
    }
  };

  const handleAcknowledgeAnomaly = async (anomalyTimestamp) => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.post(`/admin/anomalies/${anomalyTimestamp}/acknowledge`);
      await fetchMetrics();
    } catch (err) {
      console.error('Error acknowledging anomaly:', err);
    }
  };

  // Prediction handlers
  const updatePredictionConfig = (key, value) => {
    setPredictionConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const updatePredictionMetric = (metric, enabled) => {
    setPredictionConfig(prev => ({
      ...prev,
      metrics_to_predict: {
        ...prev.metrics_to_predict,
        [metric]: enabled
      }
    }));
  };

  const updatePredictionThreshold = (metric, value) => {
    setPredictionConfig(prev => ({
      ...prev,
      alert_thresholds: {
        ...prev.alert_thresholds,
        [metric]: parseInt(value)
      }
    }));
  };

  const handleSavePredictionConfig = async () => {
    setSavingPredictionConfig(true);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/admin/predictions/config', predictionConfig);
      await fetchMetrics();
    } catch (err) {
      console.error('Error saving prediction config:', err);
    } finally {
      setSavingPredictionConfig(false);
    }
  };

  const handleGeneratePredictions = async () => {
    setGeneratingPredictions(true);
    try {
      const api = (await import('../../utils/api')).default;
      const res = await api.post('/admin/predictions/generate');
      if (res.data?.warnings?.length > 0) {
        alert(`${res.data.warnings.length} prediction warning(s) generated!`);
      } else {
        alert('Predictions generated. No concerning trends detected.');
      }
      await fetchMetrics();
    } catch (err) {
      console.error('Error generating predictions:', err);
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGeneratingPredictions(false);
    }
  };

  // Utility functions
  const getSystemStatus = () => {
    if (!realtimeStats?.system) return 'unknown';
    return realtimeStats.system.status;
  };

  const getSeverityConfig = (severity) => {
    const config = {
      info: { icon: Activity, color: 'bg-blue-100 text-blue-800 border-blue-300', bgColor: 'bg-blue-50' },
      warning: { icon: AlertTriangle, color: 'bg-yellow-100 text-yellow-800 border-yellow-300', bgColor: 'bg-yellow-50' },
      critical: { icon: XCircle, color: 'bg-red-100 text-red-800 border-red-300', bgColor: 'bg-red-50' }
    };
    return config[severity] || config.info;
  };

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'alerts', label: 'Alerts', icon: Bell, count: activeAlerts.length },
    { id: 'anomalies', label: 'Anomalies', icon: Search, count: anomalyHistory.filter(a => !a.acknowledged).length },
    { id: 'predictions', label: 'Predictions', icon: TrendingUp, count: predictions?.warning_count || 0 },
    { id: 'settings', label: 'Thresholds', icon: Settings },
    { id: 'webhooks', label: 'Webhooks', icon: Link2 },
    { id: 'reports', label: 'Reports', icon: FileText }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-pink-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading system metrics...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Monitoring</h1>
            <p className="text-gray-600">Real-time performance, health metrics & alerts</p>
          </div>
          <div className="flex items-center gap-4">
            {activeAlerts.length > 0 && (
              <Badge className="bg-red-100 text-red-800 animate-pulse flex items-center gap-1">
                <BellRing className="h-4 w-4" />
                {activeAlerts.length} Active Alert{activeAlerts.length > 1 ? 's' : ''}
              </Badge>
            )}
            <StatusBadge status={getSystemStatus()} />
            <div className="flex items-center gap-2">
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={autoRefresh ? "bg-green-600 hover:bg-green-700" : ""}
              >
                {autoRefresh ? <Wifi className="h-4 w-4 mr-1" /> : <WifiOff className="h-4 w-4 mr-1" />}
                {autoRefresh ? 'Live' : 'Paused'}
              </Button>
              <Button variant="outline" size="sm" onClick={fetchMetrics}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <Card className="p-4 bg-red-50 border-red-200">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-pink-600 text-pink-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count > 0 && (
                <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5">
                  {tab.count}
                </Badge>
              )}
            </button>
          ))}
        </div>

        {/* Last Update */}
        {lastUpdate && (
          <p className="text-sm text-gray-500 flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Last updated: {lastUpdate.toLocaleTimeString()}
            {autoRefresh && <span className="text-green-600 ml-2">• Auto-refreshing every 10s</span>}
          </p>
        )}

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <MonitoringOverview
            metrics={metrics}
            realtimeStats={realtimeStats}
            activeAlerts={activeAlerts}
            onViewAlerts={() => setActiveTab('alerts')}
          />
        )}

        {activeTab === 'alerts' && (
          <MonitoringAlerts
            activeAlerts={activeAlerts}
            alertHistory={alertHistory}
            checkingAlerts={checkingAlerts}
            onManualAlertCheck={handleManualAlertCheck}
            onAcknowledgeAlert={handleAcknowledgeAlert}
            getSeverityConfig={getSeverityConfig}
          />
        )}

        {activeTab === 'anomalies' && (
          <MonitoringAnomalies
            anomalyConfig={anomalyConfig}
            anomalyHistory={anomalyHistory}
            anomalyBaselines={anomalyBaselines}
            checkingAnomalies={checkingAnomalies}
            retrainingBaselines={retrainingBaselines}
            savingAnomalyConfig={savingAnomalyConfig}
            onCheckAnomalies={handleCheckAnomalies}
            onRetrainBaselines={handleRetrainBaselines}
            onSaveAnomalyConfig={handleSaveAnomalyConfig}
            onAcknowledgeAnomaly={handleAcknowledgeAnomaly}
            updateAnomalyConfig={updateAnomalyConfig}
            updateAnomalyMetric={updateAnomalyMetric}
            getSeverityConfig={getSeverityConfig}
          />
        )}

        {activeTab === 'predictions' && (
          <MonitoringPredictions
            predictionConfig={predictionConfig}
            predictions={predictions}
            savingPredictionConfig={savingPredictionConfig}
            generatingPredictions={generatingPredictions}
            onSavePredictionConfig={handleSavePredictionConfig}
            onGeneratePredictions={handleGeneratePredictions}
            updatePredictionConfig={updatePredictionConfig}
            updatePredictionMetric={updatePredictionMetric}
            updatePredictionThreshold={updatePredictionThreshold}
            getSeverityConfig={getSeverityConfig}
          />
        )}

        {activeTab === 'settings' && (
          <MonitoringThresholds
            thresholds={thresholds}
            savingThresholds={savingThresholds}
            onSaveThresholds={handleSaveThresholds}
            updateThreshold={updateThreshold}
          />
        )}

        {activeTab === 'webhooks' && (
          <MonitoringWebhooks
            webhookConfig={webhookConfig}
            savingWebhooks={savingWebhooks}
            testingWebhook={testingWebhook}
            onSaveWebhooks={handleSaveWebhooks}
            onTestWebhook={handleTestWebhook}
            updateWebhookConfig={updateWebhookConfig}
          />
        )}

        {activeTab === 'reports' && (
          <MonitoringReports
            reportConfig={reportConfig}
            reportHistory={reportHistory}
            previewReport={previewReport}
            savingReportConfig={savingReportConfig}
            generatingReport={generatingReport}
            onSaveReportConfig={handleSaveReportConfig}
            onGenerateReport={handleGenerateReport}
            onPreviewReport={handlePreviewReport}
            onClosePreview={() => setPreviewReport(null)}
            updateReportConfig={updateReportConfig}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMonitoring;
